import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { WordpressService, WpPage, WpPageList } from '@streamamg/wordpress-lib';
import { WpPageParams } from './template.type';
import { BaseComponentDirective } from '@streamamg/amg-common';
import { DynamicComponentLoaderService } from '../dynamic-component-loader/dynamic-component-loader.service';

@Component({
  selector: 'amg-template-controller',
  templateUrl: './template-controller.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateControllerComponent extends BaseComponentDirective implements AfterViewInit {
  @ViewChild('template', {read: ViewContainerRef, static: true}) $templateRef: ViewContainerRef | undefined;
  private readonly notFoundPath = 'not-found';
  private wpPageList: WpPageList | undefined;
  public currentPath: string | undefined;
  private readonly regexRemoveSlash = /^\/+|\/+$/g;
  private readonly regexPathSegment = /\/([^\/]*)$/;

  constructor(
    private dynamicComponentLoaderService: DynamicComponentLoaderService,
    private cd: ChangeDetectorRef,
    private router: Router,
    private wordpressService: WordpressService,
  ) {
    super();
  }

  ngAfterViewInit(): void {

    this.wpPageList = this.wordpressService.getPageList();

    if (this.wpPageList) {

      this.currentPath = window.location.pathname.replace(this.regexRemoveSlash, '');

      this.defineTemplate();

      this.subSink = this.router.events
        .pipe(filter((event) => event instanceof ActivationEnd))
        .subscribe((event) => {

          const newPath = window.location.pathname.replace(this.regexRemoveSlash, '');

          if (this.currentPath !== newPath) {
            // load template only if the page is not the current one
            this.currentPath = newPath;
            this.defineTemplate();
          }
        });
    }
  }

  private defineTemplate(): void {

    let {wpPage, params} = this.recursiveRouteDiscover(this.currentPath, []);

    if (!wpPage) {

      this.router.navigate([this.notFoundPath]);
      return;
    }

    this.getWpPage(wpPage, params);
  }

  private getWpPage(wpPage: WpPage, params: string[]): void {

    this.subSink = this.wordpressService.getPageData(`?postType=${wpPage.confs.post_type}&slug=${wpPage.confs.post_slug}`)
      // @ts-ignore
      .subscribe((value: WpPage) => {

        if (value?.confs?.template || wpPage.confs.page_key) {

          this.loadTemplate(wpPage.confs.template || wpPage.confs.page_key, {
            wpData: value,
            params,
          });

        } else {

          this.router.navigate([this.notFoundPath]);
        }
      });
  }

  private recursiveRouteDiscover(path: string, params: string[]): { wpPage: WpPage | null, params?: string[] } {

    if (path === '') {
      return {wpPage: Object.values(this.wpPageList).find((wpPage) => wpPage.confs.page_key === 'home')};
    }

    if (this.wpPageList[path]) {

      return {wpPage: this.wpPageList[path], params};
    }

    const regex = new RegExp(this.regexPathSegment);
    const regexResult = regex.exec(path);

    if (regexResult) {

      if (regexResult[1]) {
        params.push(regexResult[1]);
      }

      path = path.replace(this.regexPathSegment, '');

      return this.recursiveRouteDiscover(path, params);
    }

    return {wpPage: null};
  }

  private loadTemplate(templateId: string, data: WpPageParams<unknown>): void {

    this.dynamicComponentLoaderService.loadSingleComponent(templateId, this.$templateRef, data, true);

    // set data-page
    const page = this.currentPath;

    if (page !== '/') {

      if (typeof page === 'string') {
        document.documentElement.setAttribute('data-page', page);
      }

      document.documentElement.setAttribute('data-template', templateId);
    }

    // scroll to top on page ready
    window.scrollTo({top: 0});
  }
}
